import React from 'react';
import type { LoyaltyConfig, LoyaltyGrade } from '../../../shared/types';

/** Couleurs de niveau : hex (lisible par l'app mobile) + anciens noms Tailwind. */
export const GRADE_SWATCHES: Array<{ hex: string; name: string }> = [
    { hex: '#D9A27A', name: 'Bronze' },
    { hex: '#C9CED6', name: 'Argent' },
    { hex: '#E6C35C', name: 'Or' },
    { hex: '#F2ACB9', name: 'Rose' },
    { hex: '#A6323B', name: 'Bordeaux' },
];
const LEGACY: Record<string, string> = { amber: '#D9A27A', sky: '#C9CED6', purple: '#E6C35C', emerald: '#F2ACB9', rose: '#A6323B', gray: '#C9CED6' };
export const gradeHex = (g: Pick<LoyaltyGrade, 'color'>, idx = 0) =>
    (g.color && g.color.startsWith('#')) ? g.color : LEGACY[g.color ?? ''] ?? GRADE_SWATCHES[idx % GRADE_SWATCHES.length].hex;

interface Props {
    config: LoyaltyConfig;
    restaurantName: string;
}

/**
 * La carte de fidélité telle que le client la verra dans l'app, mise à jour
 * en direct pendant la configuration — comme l'aperçu de la fiche
 * « Programme » du site.
 */
const ProgramPreview: React.FC<Props> = ({ config, restaurantName }) => {
    const isStamps = config.type === 'stamps';
    const target = Math.max(1, config.targetCount ?? (isStamps ? 10 : 50));
    const grades = config.grades ?? [];
    const topGrade = grades[grades.length - 1];
    // Progression de démonstration : ~60 % de l'objectif
    const progress = isStamps ? Math.min(target, Math.max(1, Math.round(target * 0.6))) : Math.round(target * 0.6);
    const boostLabel = !config.boost?.enabled ? null
        : config.boost.mode === 'initial_stamps' ? `${config.boost.initialStamps ?? 1} tampon${(config.boost.initialStamps ?? 1) > 1 ? 's' : ''} offert${(config.boost.initialStamps ?? 1) > 1 ? 's' : ''} à l'inscription`
            : config.boost.mode === 'first_week' ? 'Points doublés la première semaine'
                : 'Premier passage boosté';
    const tiers = (config.rewardTiers ?? []).slice().sort((a, b) => a.threshold - b.threshold);

    return (
        <div className="mx-auto w-[300px]">
            <div className="rounded-[40px] bg-[#1f1213] p-[10px] shadow-xl">
                <div className="h-[560px] overflow-hidden rounded-[32px] bg-cream">
                    <div className="px-5 pt-7 pb-4">
                        <div className="mx-auto mb-5 h-1.5 w-20 rounded-full bg-black/10" />
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-gray-500">Ma carte</p>
                                <p className="truncate font-display text-lg leading-tight text-gray-900">{restaurantName}</p>
                            </div>
                            {topGrade && (
                                <span className="inline-flex h-6 items-center rounded-full px-2.5 text-xs font-bold text-gray-900" style={{ background: gradeHex(topGrade, grades.length - 1) }}>
                                    {topGrade.name}
                                </span>
                            )}
                        </div>

                        <div className="mt-4 rounded-3xl bg-white p-4 shadow-sm border border-black/[0.06]">
                            {isStamps ? (
                                <>
                                    <div className="grid grid-cols-5 gap-2">
                                        {Array.from({ length: target }).map((_, i) => (
                                            <span key={i} className={`aspect-square rounded-full ${i < progress ? 'bg-brand-500' : 'border-2 border-dashed border-gray-300'}`} />
                                        ))}
                                    </div>
                                    <p className="mt-3 text-sm text-gray-700">
                                        <b>{progress} / {target}</b> tampons · {config.rewardLabel || 'récompense'} au {target}<sup>e</sup>
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-end justify-between">
                                        <p className="font-stat text-3xl text-gray-900">{progress}<span className="ml-1 text-sm text-gray-500">pts</span></p>
                                        <p className="text-xs text-gray-500">objectif {target} pts</p>
                                    </div>
                                    <div className="mt-2 h-2 rounded-full bg-cream-200">
                                        <div className="h-2 rounded-full bg-brand-500" style={{ width: `${Math.min(100, (progress / target) * 100)}%` }} />
                                    </div>
                                    <p className="mt-2 text-sm text-gray-700">
                                        {config.generosity ?? 1} pt{(config.generosity ?? 1) > 1 ? 's' : ''} par euro · <b>{config.rewardLabel || 'récompense'}</b> à {target} pts
                                    </p>
                                </>
                            )}
                            {boostLabel && (
                                <p className="mt-3 inline-flex rounded-full bg-peach-100 px-2.5 py-1 text-xs font-semibold text-brand-700">Boost : {boostLabel}</p>
                            )}
                        </div>

                        {tiers.length > 0 && (
                            <div className="mt-4">
                                <p className="eyebrow mb-2">Récompenses</p>
                                <ul className="space-y-1.5">
                                    <li className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-sm border border-black/[0.06]">
                                        <span className="font-medium text-gray-900">{config.rewardLabel}</span>
                                        <span className="text-xs text-gray-500">{target} {isStamps ? 'tampons' : 'pts'}</span>
                                    </li>
                                    {tiers.map((t) => (
                                        <li key={t.threshold} className="flex items-center justify-between rounded-xl bg-white px-3 py-2 text-sm border border-black/[0.06]">
                                            <span className="font-medium text-gray-900">{t.label || 'Récompense'}</span>
                                            <span className="text-xs text-gray-500">{t.threshold} {isStamps ? 'tampons' : 'pts'}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {grades.length > 1 && (
                            <div className="mt-4">
                                <p className="eyebrow mb-2">Niveaux</p>
                                <div className="flex gap-1.5">
                                    {grades.map((g, i) => (
                                        <span key={g.id} className="flex-1 rounded-full px-2 py-1 text-center text-xs font-semibold text-gray-900 truncate" style={{ background: gradeHex(g, i), opacity: i === 0 ? 1 : 0.9 }} title={`${g.name} dès ${g.threshold}`}>
                                            {g.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <p className="mt-3 text-center text-xs text-gray-500">Aperçu côté client — se met à jour en direct.</p>
        </div>
    );
};

export default ProgramPreview;
